/**
 * <SamPeek> — ce que Sam vient de dire, feuille fermée (molecule).
 *
 * Une bulle courte à gauche de sa tuile, sa queue tournée vers lui : son bonjour au bout de
 * quelques secondes, sa relance, ou le début de sa dernière réponse. Sam n'ouvre jamais la
 * conversation de lui-même : il glisse cet aperçu, et c'est l'élève qui décide.
 *
 * Posée au-dessus de Sam plutôt qu'à côté (la scène du live, Wajih 22/10 : la bulle sort près de celui
 * qui est devant), sa queue passe au coin bas gauche, au-dessus de lui, et la croix de l'autre côté
 * (`tail="left"`).
 *
 * Toute la bulle presse et ouvre la conversation ; la croix la referme sans rien ouvrir (elle
 * tient quelques secondes, ce qui est long quand on lit un énoncé juste derrière). La durée et
 * la disparition sont une affaire de l'écran : ici la bulle est là, ou pas.
 *
 * Le texte arrive déjà coupé (`utils/samPeek`). La surface est celle de ce qui flotte
 * (`surface-floating`, sans couleur de champion) : elle passe sur l'énoncé, ses images, une carte.
 * En clair c'est la carte, et son ombre la détache ; sur la nuit l'ombre ne se voit pas, et la
 * bulle monte d'une marche de clarté — à la surface de la carte, elle se fondait dans la page.
 *
 * Composes DS pieces only — `<Card>`, `<Text>`, `<IconButton>`, `<Row>`.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { Card } from "../Card"
import { TOUCH_TARGET } from "../../utils/touchTarget"
import { SAM_PEEK_MAX_WIDTH } from "./samPeekGeometry"

const TEXTS = {
    dismiss: "Fermer l'aperçu",
}

const DISMISS_SIZE = 28

export type SamPeekProps = {
    /** Ce que Sam dit, en texte simple, déjà coupé. Vide : rien ne se rend. */
    text: string
    /** Toute la bulle ouvre la conversation. */
    onPress: () => void
    /** La croix. Absente : pas de croix, l'aperçu s'en va seul. */
    onDismiss?: () => void
    /**
     * Le coin de la queue, tourné vers Sam (comme `<MessageBubble>`) : `right` (défaut), Sam à droite de la
     * bulle ; `left`, Sam sous elle à gauche, la croix passe à droite.
     */
    tail?: "left" | "right"
    testID?: string
}

export function SamPeek(props: SamPeekProps) {
    const { text, onPress, onDismiss, tail = "right", testID } = props

    if (text === "") return null

    const dismiss = onDismiss != null && (
        // La cible de 44 px presse comme la croix qu'elle entoure, sans être un second bouton pour un
        // lecteur d'écran.
        <Pressable onPress={onDismiss} accessible={false} focusable={false} style={styles.target}>
            <IconButton
                iconName="times"
                size={DISMISS_SIZE}
                color="surface-floating"
                iconColor="text-muted"
                onPress={onDismiss}
                accessibilityLabel={TEXTS.dismiss}
            />
        </Pressable>
    )

    return (
        <Row gap="0" align="end" style={styles.root} testID={testID}>
            {tail === "right" && dismiss}
            <Card
                pressable
                onPress={onPress}
                background="surface-floating"
                elevation="floating"
                style={[styles.bubble, tail === "left" ? styles.tailLeft : styles.tailRight]}
            >
                <Text size="text-md">{frenchSpacing(text)}</Text>
            </Card>
            {tail === "left" && dismiss}
        </Row>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        flexShrink: 1,
    },
    target: {
        width: TOUCH_TARGET,
        height: TOUCH_TARGET,
        alignItems: "center",
        justifyContent: "center",
    },
    bubble: {
        flexShrink: 1,
        maxWidth: SAM_PEEK_MAX_WIDTH,
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
    },
    /** La queue : le coin tourné vers la tuile se resserre, comme sur une bulle du fil. */
    tailRight: { borderBottomRightRadius: theme.radius.sm / 2 },
    tailLeft: { borderBottomLeftRadius: theme.radius.sm / 2 },
}))
